import { ErrorMessage } from '@hookform/error-message';
import ReactButton from 'components/Buttons/ReactButton';
import { ErrorFormMessage } from 'components/ErrorFormMessage';
import FlexSelect from 'components/Inputs/FlexSelect';
import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Col, Form, Input, Row, Button } from 'reactstrap';
import AddressService from 'services/addressService';
import ContactService from 'views/CRM/Contact/services/contactService';
import AccountService from 'views/CRM/Account/services/accountService';
import { PiHouseLineBold } from 'react-icons/pi';
import { brazilianStates } from 'constants/brazil_states';

/**
 * Converte uma data ISO (timestamp) para o formato brasileiro DD/MM/YYYY
 * @param {string|Date} dateValue - Data em formato ISO ou objeto Date
 * @returns {string} Data formatada no padrão brasileiro
 */

const formatDateToBrazilian = (dateValue) => {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const AddressCard = ({
  address,
  isSelected,
  onSelectionChange,
  showSourceLabel = true,
  singleSelect = false
}) => {
  const formatTitle = (name = '') => {
    if (name.length <= 26) return name;
    return `${name.substring(0, 24).trim()}...`;
  };

  const cardStyle = {
    position: 'relative',
    border: isSelected ? '2px solid #1f69c0' : '1px solid #c5d9e5',
    borderRadius: '0.5rem',
    padding: '0.8rem',
    width: '24%',
    minWidth: '280px',
    overflow: 'hidden',
    backgroundColor: isSelected ? '#f8f9ff' : '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  return (
    <div style={cardStyle} onClick={() => onSelectionChange(address.id)}>
      <div
        style={{
          position: 'absolute',
          right: '0.5rem',
          top: '0.5rem'
        }}
      >
        <input
          type={singleSelect ? 'radio' : 'checkbox'}
          name={singleSelect ? 'selectedAddress' : undefined}
          checked={isSelected}
          onChange={() => onSelectionChange(address.id)}
          style={{ transform: 'scale(1.2)' }}
        />
      </div>

      <div>
        <span title={address.title}>
          <PiHouseLineBold /> {formatTitle(address.title || 'Sem título')}
        </span>
      </div>

      {showSourceLabel && address.sourceName && (
        <div
          className="small mb-1"
          style={{ color: '#1f69c0', fontWeight: 'bold' }}
        >
          <span>{address.sourceName}</span>
        </div>
      )}

      <div className="small">
        <span>
          <span className="font-weight-bold">CEP: </span>
          {address.zipCode || ''}
        </span>
      </div>
      <div className="small">
        <span className="font-weight-bold">Estado: </span>
        <span>{address.state || ''}</span>
      </div>
      <div className="small">
        <span className="font-weight-bold">Cidade: </span>
        <span>{address.city || ''}</span>
      </div>
      <div className="small">
        <span className="font-weight-bold">Bairro: </span>
        <span>{address.neighborhood || ''}</span>
      </div>
      <div className="small">
        <span className="font-weight-bold">Rua: </span>
        <span>{address.street || ''}</span>
      </div>
      <div className="small">
        <span className="font-weight-bold">Número: </span>
        <span>{address.number || 'S/N'}</span>
      </div>
      <div className="small">
        <span className="font-weight-bold">Complemento: </span>
        <span>{address.complement || ''}</span>
      </div>
    </div>
  );
};

const getEntityConfig = (entityType) => {
  if (entityType === 'contact') {
    return {
      relationKey: 'accounts',
      relationLabel: 'contas',
      relationLabelSingle: 'Conta',
      sourcePrefix: 'Conta:',
      noRelationMessage: 'Este contato não possui contas vinculadas.',
      noAddressesMessage:
        'Nenhum endereço novo encontrado nas contas deste contato.',
      alreadySharedMessage:
        'Todos os endereços das contas já estão compartilhados com este contato.',
      shareButtonText: 'Importar endereços das contas',
      singleAddress: false,
      Service: ContactService,
      getEntityById: async (recordTypeId, id) => {
        const contactResponse = await ContactService.getContactById(id);
        const addressesResponse = await ContactService.getContactAddresses(id);

        const contact = contactResponse.data.data;
        contact.addresses = addressesResponse.data.data || [];

        return { data: { data: contact } };
      },
      updateEntity: (recordTypeId, id, payload) =>
        ContactService.updateContact(recordTypeId, id, payload),
      getRelatedAddresses: async (relatedEntity) => {
        const response = await AccountService.getById(
          relatedEntity.recordTypeId,
          relatedEntity.accountId || relatedEntity.id
        );
        return response.data.data?.addresses || [];
      },
      buildUpdatePayload: (entity, addressIds) => ({
        name: entity.name,
        phone: entity.phone,
        ddi: entity.ddi,
        email: entity.email || '',
        accountIds: entity.accounts?.map((acc) => acc.accountId) || [],
        addressIds: addressIds,
        tags: entity.tags?.map((tag) => tag.id) || []
      }),
      getRelatedEntityName: (relatedEntity) =>
        relatedEntity.accountName || relatedEntity.name
    };
  } else if (entityType === 'account') {
    return {
      relationKey: 'contacts',
      relationLabel: 'contatos',
      relationLabelSingle: 'Contato',
      sourcePrefix: 'Contato:',
      noRelationMessage: 'Esta conta não possui contatos vinculados.',
      noAddressesMessage:
        'Nenhum endereço novo encontrado nos contatos desta conta.',
      alreadySharedMessage:
        'Todos os endereços dos contatos já estão compartilhados com esta conta.',
      shareButtonText: 'Importar endereços dos contatos',
      singleAddress: false,
      Service: AccountService,
      getEntityById: (recordTypeId, id) =>
        AccountService.getById(recordTypeId, id),
      updateEntity: (recordTypeId, id, payload) =>
        AccountService.update(id, payload),
      getRelatedAddresses: async (relatedEntity) => {
        const response = await ContactService.getContactAddresses(
          relatedEntity.id
        );
        return response.data.data || [];
      },
      buildUpdatePayload: (entity, addressIds) => ({
        ...entity,
        addressIds: addressIds
      }),
      getRelatedEntityName: (relatedEntity) => relatedEntity.name
    };
  } else {
    const OpportunityService =
      require('views/CRM/Opportunity/services/opportunityService').default;
    return {
      relationKeys: ['account', 'contact'],
      relationLabel: 'conta e contato',
      relationLabelSingle: 'Conta/Contato',
      sourcePrefix: '',
      noRelationMessage:
        'Esta oportunidade não possui conta ou contato vinculado.',
      noAddressesMessage:
        'Nenhum endereço encontrado na conta ou contato desta oportunidade.',
      alreadySharedMessage:
        'A oportunidade já possui um endereço vinculado. Selecione outro para substituir.',
      shareButtonText: 'Selecionar endereço da conta/contato',
      singleAddress: true,
      Service: OpportunityService,
      getEntityById: async (recordTypeId, id) => {
        const oppResponse = await OpportunityService.getById(recordTypeId, id);
        const addressesResponse =
          await OpportunityService.getOpportunityAddresses(id);

        const opportunity = oppResponse.data.data;
        opportunity.addresses = addressesResponse.data.data || [];

        return { data: { data: opportunity } };
      },
      updateEntity: (recordTypeId, id, payload) =>
        OpportunityService.updateOpportunity(recordTypeId, id, payload),
      getRelatedAddresses: async (relatedEntity, type) => {
        if (type === 'account' && relatedEntity.accountId) {
          const response = await AccountService.getById(
            relatedEntity.accountRecordTypeId,
            relatedEntity.accountId
          );
          return response.data.data?.addresses || [];
        } else if (type === 'contact' && relatedEntity.contactId) {
          const response = await ContactService.getContactAddresses(
            relatedEntity.contactId
          );
          return response.data.data || [];
        }
        return [];
      },
      buildUpdatePayload: (entity, addressIds) => {
        const addressId = Array.isArray(addressIds)
          ? addressIds[0]
          : addressIds;

        const basePayload = {
          title: entity.title,
          amount: entity.amount,
          expectedClosure: formatDateToBrazilian(entity.expectedClosure),
          accountId: entity.accountId,
          contactId: entity.contactId,
          addressId: addressId || null
        };

        const excludedFields = [
          'title',
          'amount',
          'expectedClosure',
          'accountId',
          'contactId',
          'addressId',
          'addressIds',
          'addresses',
          'stageId',
          'recordTypeId',
          'id',
          'catalog',
          'schedulingCrm',
          'attachments',
          'responsible',
          'responsibleLogin',
          'createdByLogin',
          'createdAt',
          'lastUpdatedByLogin',
          'lastUpdatedAt',
          'accountName',
          'contactName',
          'accountRecordTypeId',
          'temperature',
          'lostReason',
          'commentLostReason',
          'stagesFree',
          'wasCreatedWithProduct',
          'convertOpportunityId'
        ];

        Object.keys(entity).forEach((key) => {
          if (!excludedFields.includes(key)) {
            basePayload[key] = entity[key];
          }
        });

        const payload = {};
        Object.keys(basePayload).forEach((key) => {
          if (basePayload[key] !== undefined) {
            payload[key] = basePayload[key];
          }
        });

        return payload;
      },
      getRelatedEntityName: (relatedEntity, type) => {
        if (type === 'account') return relatedEntity.accountName || '';
        if (type === 'contact') return relatedEntity.contactName || '';
        return '';
      }
    };
  }
};

const AddAddressForm = ({
  entityType = 'contact',
  entityId,
  entity,
  onAddedAddress,
  onCancel
}) => {
  const { control, errors, setValue, handleSubmit, reset, watch } = useForm();
  const [withoutNumber, setWithoutNumber] = useState(false);
  const [addressMode, setAddressMode] = useState('new');
  const [relatedAddresses, setRelatedAddresses] = useState([]);
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);

  const config = getEntityConfig(entityType);
  const zipCodeValue = watch('zipCode');

  useEffect(() => {
    fetchRelatedAddresses();
  }, [entity, entityType]);

  useEffect(() => {
    if (zipCodeValue && zipCodeValue.length === 8) {
      fetchAddressByCep(zipCodeValue);
    }
  }, [zipCodeValue]);

  const fetchAddressByCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) return;

    setLoadingCep(true);

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cleanCep}/json/`
      );
      const data = await response.json();

      if (data.erro) {
        toast.warning('CEP não encontrado. Preencha os campos manualmente.');
        setLoadingCep(false);
        return;
      }

      if (data.uf) {
        setValue('state', data.uf);
      }
      if (data.localidade) {
        setValue('city', data.localidade);
      }
      if (data.bairro) {
        setValue('neighborhood', data.bairro);
      }
      if (data.logradouro) {
        setValue('street', data.logradouro);
      }
      if (data.complemento) {
        setValue('complement', data.complemento);
      }

      toast.success('Endereço preenchido automaticamente!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);

      if (error.message?.includes('429') || error.status === 429) {
        toast.warning(
          'Limite de consultas de CEP atingido. Preencha os campos manualmente.'
        );
      } else {
        toast.info(
          'Não foi possível buscar o CEP automaticamente. Você pode preencher manualmente.'
        );
      }
    } finally {
      setLoadingCep(false);
    }
  };

  const fetchRelatedAddresses = async () => {
    if (entityType === 'opportunity') {
      try {
        setLoadingAddresses(true);

        const fullEntityResponse = await config.getEntityById(
          entity.recordTypeId,
          entityId
        );
        const currentEntity = fullEntityResponse.data.data;

        const allAddresses = [];
        const existingAddressIds =
          currentEntity.addresses?.map((addr) => addr.id) || [];

        if (currentEntity.accountId) {
          try {
            const accountAddresses = await config.getRelatedAddresses(
              currentEntity,
              'account'
            );
            const accountName = currentEntity.accountName || 'Conta';

            const availableAccountAddresses = accountAddresses.filter(
              (addr) => !existingAddressIds.includes(addr.id)
            );

            allAddresses.push(
              ...availableAccountAddresses.map((addr) => ({
                ...addr,
                relatedEntityName: accountName,
                relatedEntityId: currentEntity.accountId,
                sourceName: `Conta: ${accountName}`,
                displayName: `${addr.title || 'Sem título'} - ${addr.street}, ${
                  addr.number || 'S/N'
                }, ${addr.city}/${addr.state} (Conta: ${accountName})`
              }))
            );
          } catch (err) {
            console.warn('Erro ao buscar endereços da conta:', err);
          }
        }

        if (currentEntity.contactId) {
          try {
            const contactAddresses = await config.getRelatedAddresses(
              currentEntity,
              'contact'
            );
            const contactName = currentEntity.contactName || 'Contato';

            const availableContactAddresses = contactAddresses.filter(
              (addr) => !existingAddressIds.includes(addr.id)
            );

            allAddresses.push(
              ...availableContactAddresses.map((addr) => ({
                ...addr,
                relatedEntityName: contactName,
                relatedEntityId: currentEntity.contactId,
                sourceName: `Contato: ${contactName}`,
                displayName: `${addr.title || 'Sem título'} - ${addr.street}, ${
                  addr.number || 'S/N'
                }, ${addr.city}/${addr.state} (Contato: ${contactName})`
              }))
            );
          } catch (err) {
            console.warn('Erro ao buscar endereços do contato:', err);
          }
        }

        setRelatedAddresses(allAddresses);
      } catch (error) {
        console.error(
          'Erro ao buscar endereços da conta/contato da oportunidade:',
          error
        );
        toast.error('Erro ao buscar endereços da conta/contato');
        setRelatedAddresses([]);
      } finally {
        setLoadingAddresses(false);
      }
      return;
    }

    const relatedEntities = entity[config.relationKey];

    if (!relatedEntities?.length) {
      try {
        const fullEntityResponse = await config.getEntityById(
          entity.recordTypeId,
          entityId
        );
        const fullEntity = fullEntityResponse.data.data;

        if (!fullEntity[config.relationKey]?.length) {
          return;
        }

        entity[config.relationKey] = fullEntity[config.relationKey];
      } catch (error) {
        console.error('Erro ao buscar dados completos da entidade:', error);
        return;
      }
    }

    try {
      setLoadingAddresses(true);

      const fullEntityResponse = await config.getEntityById(
        entity.recordTypeId,
        entityId
      );
      const currentEntity = fullEntityResponse.data.data;

      const relatedEntities = currentEntity[config.relationKey];

      if (!relatedEntities?.length) {
        setRelatedAddresses([]);
        return;
      }

      const allAddresses = [];

      const existingAddressIds =
        currentEntity.addresses?.map((addr) => addr.id) || [];

      for (const relatedEntity of relatedEntities) {
        try {
          const addresses = await config.getRelatedAddresses(relatedEntity);
          const relatedName = config.getRelatedEntityName(relatedEntity);

          const availableAddresses = addresses.filter(
            (addr) => !existingAddressIds.includes(addr.id)
          );

          allAddresses.push(
            ...availableAddresses.map((addr) => ({
              ...addr,
              relatedEntityName: relatedName,
              relatedEntityId: relatedEntity.id,
              sourceName: `${config.sourcePrefix} ${relatedName}`,
              displayName: `${addr.title || 'Sem título'} - ${addr.street}, ${
                addr.number || 'S/N'
              }, ${addr.city}/${addr.state} (${
                config.relationLabelSingle
              }: ${relatedName})`
            }))
          );
        } catch (err) {
          console.warn(
            `Erro ao buscar endereços de ${config.getRelatedEntityName(
              relatedEntity
            )}:`,
            err
          );
        }
      }

      setRelatedAddresses(allAddresses);
    } catch (error) {
      console.error(
        `Erro ao buscar endereços dos ${config.relationLabel}:`,
        error
      );
      toast.error(`Erro ao buscar endereços dos ${config.relationLabel}`);
      setRelatedAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleAddressSelection = (addressId) => {
    if (config.singleAddress) {
      setSelectedAddresses([addressId]);
    } else {
      setSelectedAddresses((prev) => {
        if (prev.includes(addressId)) {
          return prev.filter((id) => id !== addressId);
        } else {
          return [...prev, addressId];
        }
      });
    }
  };

  const handleShareSelectedAddresses = async () => {
    if (selectedAddresses.length === 0) {
      toast.error('Selecione pelo menos um endereço para compartilhar');
      return;
    }

    try {
      const currentEntityResponse = await config.getEntityById(
        entity.recordTypeId,
        entityId
      );
      const currentEntity = currentEntityResponse.data.data;

      const existingAddressIds =
        currentEntity.addresses?.map((addr) => addr.id) || [];

      const alreadyLinkedAddresses = selectedAddresses.filter((id) =>
        existingAddressIds.includes(id)
      );
      if (alreadyLinkedAddresses.length > 0) {
        toast.warning(
          `${alreadyLinkedAddresses.length} endereço(s) já vinculado(s)`
        );
      }

      const newAddressIds = selectedAddresses.filter(
        (id) => !existingAddressIds.includes(id)
      );
      if (newAddressIds.length === 0) {
        toast.info('Todos os endereços selecionados já estão vinculados');
        return;
      }

      const updatedAddressIds = config.singleAddress
        ? [newAddressIds[0]]
        : [...existingAddressIds, ...newAddressIds];

      const updatePayload = config.buildUpdatePayload(
        currentEntity,
        updatedAddressIds
      );

      await config.updateEntity(
        currentEntity.recordTypeId,
        entityId,
        updatePayload
      );

      toast.success(
        `${newAddressIds.length} endereço(s) compartilhado(s) com sucesso`
      );
      setSelectedAddresses([]);
      onAddedAddress();
    } catch (error) {
      console.error('Erro ao importar endereços:', error);
      toast.error(
        error?.response?.data?.message || 'Erro ao importar endereços'
      );
    }
  };

  const onSubmitCreateNew = async (data) => {
    try {
      const addressData = {
        ...data,
        zipCode: data.zipCode,
        number: withoutNumber ? null : Number(data.number) || null
      };

      const createAddressResponse = await AddressService.createAddress(
        addressData
      );
      const newAddressId = createAddressResponse.data.data.id;

      const currentEntityResponse = await config.getEntityById(
        entity.recordTypeId,
        entityId
      );
      const currentEntity = currentEntityResponse.data.data;

      const existingAddressIds = currentEntity.addresses
        ? currentEntity.addresses.map((addr) => addr.id)
        : [];

      const updatedAddressIds = config.singleAddress
        ? [newAddressId]
        : [...existingAddressIds, newAddressId];

      const updatePayload = config.buildUpdatePayload(
        currentEntity,
        updatedAddressIds
      );

      await config.updateEntity(
        currentEntity.recordTypeId,
        entityId,
        updatePayload
      );

      toast.success('Endereço criado e vinculado com sucesso');
      reset();
      onAddedAddress();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao criar endereço');
    }
  };

  const hasRelatedEntities =
    entityType === 'opportunity'
      ? entity.accountId || entity.contactId
      : entity[config.relationKey]?.length > 0;

  return (
    <div>
      {hasRelatedEntities && (
        <Row>
          <Col className="mb-3" sm="12">
            <div className="custom-control custom-radio mb-2">
              <input
                type="radio"
                id="addressModeExisting"
                name="addressMode"
                className="custom-control-input"
                value="existing"
                checked={addressMode === 'existing'}
                onChange={() => setAddressMode('existing')}
              />
              <label
                className="custom-control-label"
                htmlFor="addressModeExisting"
              >
                {config.shareButtonText}
                {relatedAddresses.length > 0 && (
                  <small className="ml-2 text-muted">
                    ({relatedAddresses.length} disponíveis)
                  </small>
                )}
              </label>
            </div>
            <div className="custom-control custom-radio">
              <input
                type="radio"
                id="addressModeNew"
                name="addressMode"
                className="custom-control-input"
                value="new"
                checked={addressMode === 'new'}
                onChange={() => setAddressMode('new')}
              />
              <label className="custom-control-label" htmlFor="addressModeNew">
                Criar novo endereço
              </label>
            </div>
          </Col>
        </Row>
      )}

      {addressMode === 'existing' && hasRelatedEntities && (
        <div className="existing-addresses">
          {loadingAddresses ? (
            <div className="text-center p-4">
              <p>Carregando endereços dos {config.relationLabel}...</p>
            </div>
          ) : relatedAddresses.length === 0 ? (
            <div
              className="text-center p-4 rounded"
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6'
              }}
            >
              <p className="mb-1">{config.noAddressesMessage}</p>
              <small className="text-muted">
                {config.alreadySharedMessage}
              </small>
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  Endereços disponíveis para compartilhar:
                </h5>
                <div>
                  <small className="text-muted me-3">
                    {selectedAddresses.length} selecionado(s)
                  </small>
                  {selectedAddresses.length > 0 && (
                    <Button
                      size="sm"
                      color="link"
                      className="p-0"
                      onClick={() => setSelectedAddresses([])}
                    >
                      Limpar seleção
                    </Button>
                  )}
                </div>
              </div>

              <div
                className="address-list"
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  maxHeight: '500px',
                  overflowY: 'auto',
                  padding: '1rem',
                  border: '1px solid #e9ecef',
                  borderRadius: '0.375rem',
                  backgroundColor: '#f8f9fa'
                }}
              >
                {relatedAddresses.map((address) => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    isSelected={selectedAddresses.includes(address.id)}
                    onSelectionChange={handleAddressSelection}
                    showSourceLabel={true}
                    singleSelect={config.singleAddress}
                  />
                ))}
              </div>

              <div className="mt-3 d-flex justify-content-end gap-2">
                <Button color="secondary" onClick={onCancel}>
                  Cancelar
                </Button>
                <Button
                  color="primary"
                  onClick={handleShareSelectedAddresses}
                  disabled={selectedAddresses.length === 0}
                >
                  {config.singleAddress
                    ? 'Selecionar Endereço'
                    : `Compartilhar ${selectedAddresses.length} Endereço(s)`}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {(addressMode === 'new' || !hasRelatedEntities) && (
        <Form
          className="needs-validation"
          onSubmit={handleSubmit(onSubmitCreateNew)}
        >
          <Row>
            <Col className="mb-3" sm="12">
              <label className="form-control-label text-capitalize">
                Título*
              </label>
              <Controller
                render={(props) => (
                  <Input
                    value={props.value}
                    onChange={props.onChange}
                    type="text"
                    placeholder="Ex: Casa, Trabalho, Endereço principal..."
                  />
                )}
                control={control}
                defaultValue=""
                rules={{ required: 'Campo obrigatório' }}
                name="title"
              />
              <ErrorMessage
                errors={errors}
                name="title"
                render={({ message }) => ErrorFormMessage({ message })}
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-3" sm="4">
              <label className="form-control-label text-capitalize">
                CEP*
                {loadingCep && (
                  <small className="ml-2 text-primary">
                    <span
                      className="spinner-border spinner-border-sm mr-1"
                      role="status"
                    />
                    Buscando...
                  </small>
                )}
              </label>
              <Controller
                render={(props) => (
                  <Input
                    value={props.value}
                    onChange={props.onChange}
                    type="text"
                    placeholder="00000-000"
                    maxLength={8}
                    disabled={loadingCep}
                  />
                )}
                control={control}
                defaultValue=""
                rules={{ required: 'Campo obrigatório' }}
                name="zipCode"
              />
              <ErrorMessage
                errors={errors}
                name="zipCode"
                render={({ message }) => ErrorFormMessage({ message })}
              />
            </Col>

            <Col className="mb-3" sm="4">
              <label className="form-control-label text-capitalize">
                Estado*
              </label>
              <Controller
                render={(props) => (
                  <FlexSelect
                    dataOptions={brazilianStates}
                    isMulti={false}
                    value={props.value}
                    valueController={setValue}
                    fieldName="state"
                    labelName="name"
                    valueName="id"
                    isClearable={true}
                    closeMenuOnSelect={true}
                    placeholder="Selecione o estado"
                  />
                )}
                control={control}
                defaultValue=""
                rules={{ required: 'Campo obrigatório' }}
                name="state"
              />
              <ErrorMessage
                errors={errors}
                name="state"
                render={({ message }) => ErrorFormMessage({ message })}
              />
            </Col>

            <Col className="mb-3" sm="4">
              <label className="form-control-label text-capitalize">
                Cidade*
              </label>
              <Controller
                render={(props) => (
                  <Input
                    value={props.value}
                    onChange={props.onChange}
                    type="text"
                    placeholder="Digite a cidade"
                  />
                )}
                control={control}
                defaultValue=""
                rules={{ required: 'Campo obrigatório' }}
                name="city"
              />
              <ErrorMessage
                errors={errors}
                name="city"
                render={({ message }) => ErrorFormMessage({ message })}
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-3" sm="6">
              <label className="form-control-label text-capitalize">
                Bairro*
              </label>
              <Controller
                render={(props) => (
                  <Input
                    value={props.value}
                    onChange={props.onChange}
                    type="text"
                    placeholder="Digite o bairro"
                  />
                )}
                control={control}
                defaultValue=""
                rules={{ required: 'Campo obrigatório' }}
                name="neighborhood"
              />
              <ErrorMessage
                errors={errors}
                name="neighborhood"
                render={({ message }) => ErrorFormMessage({ message })}
              />
            </Col>

            <Col className="mb-3" sm="6">
              <label className="form-control-label text-capitalize">Rua</label>
              <Controller
                render={(props) => (
                  <Input
                    value={props.value}
                    onChange={props.onChange}
                    type="text"
                    placeholder="Digite a rua"
                  />
                )}
                control={control}
                defaultValue=""
                name="street"
              />
            </Col>
          </Row>

          <Row>
            <Col className="mb-3" sm="6">
              <label className="form-control-label text-capitalize">
                Número
              </label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}
              >
                <input
                  type="checkbox"
                  id="withoutNumber"
                  checked={withoutNumber}
                  onChange={(e) => setWithoutNumber(e.target.checked)}
                />
                <label htmlFor="withoutNumber">S/N (sem número)</label>
              </div>
              {!withoutNumber && (
                <Controller
                  render={(props) => (
                    <Input
                      value={props.value}
                      onChange={props.onChange}
                      type="number"
                      placeholder="Digite o número"
                      min="0"
                    />
                  )}
                  control={control}
                  defaultValue=""
                  name="number"
                />
              )}
            </Col>

            <Col className="mb-3" sm="6">
              <label className="form-control-label text-capitalize">
                Complemento
              </label>
              <Controller
                render={(props) => (
                  <Input
                    value={props.value}
                    onChange={props.onChange}
                    type="text"
                    placeholder="Apartamento, bloco, etc."
                  />
                )}
                control={control}
                defaultValue=""
                name="complement"
              />
            </Col>
          </Row>

          <Row className="d-flex justify-content-end">
            <Col sm="6" className="text-right">
              <ReactButton
                type="button"
                color="secondary"
                onClick={onCancel}
                style={{ marginRight: '8px' }}
              >
                Cancelar
              </ReactButton>
              <ReactButton type="submit" color="primary">
                Criar endereço
              </ReactButton>
            </Col>
          </Row>
        </Form>
      )}
    </div>
  );
};

export default AddAddressForm;
